import { useState } from 'react';

import { useFragment as useApolloFragment } from '@apollo/client/react';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { Pencil } from 'lucide-react';

import { PROPERTY_ADDRESS_FRAGMENT } from '@modules/_shared/components/property-form/propertyFormQueries';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { PropertyAddressDialog } from '@modules/reports/shared/PropertyAddressDialog';
import { useLocale } from '@shared/hooks/locale';

type PropertyAddressCompactProps = {
  propertyId: string;
  onPropertyUpdate: () => void | Promise<void>;
};

export const PropertyAddressCompact = ({
  propertyId,
  onPropertyUpdate,
}: PropertyAddressCompactProps) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const addressFragment = useApolloFragment({
    fragment: PROPERTY_ADDRESS_FRAGMENT,
    fragmentName: 'PropertyAddress',
    from: { __typename: 'properties', id: propertyId },
  });

  const loading = !addressFragment.complete;
  const property = addressFragment.complete ? addressFragment.data : null;
  const formattedAddress =
    property != null ? formatAddress(property, ', ') : '';
  const hasAddress = formattedAddress.trim().length > 0;

  return (
    <Box sx={{ p: 2, pb: 0 }}>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0.75,
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
          {t('Address')}
        </Typography>
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        {loading ? (
          <Skeleton width="60%" />
        ) : (
          <Typography
            variant="body2"
            color={hasAddress ? 'text.primary' : 'text.secondary'}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {hasAddress ? formattedAddress : t('No address')}
          </Typography>
        )}
        <Button
          size="small"
          variant="text"
          startIcon={<Pencil size={16} />}
          onClick={() => setOpen(true)}
          disabled={loading || property == null}
          sx={{ flexShrink: 0 }}
        >
          {hasAddress ? t('Edit') : t('Add address')}
        </Button>
      </Stack>
      {property != null && (
        <PropertyAddressDialog
          open={open}
          propertyId={propertyId}
          defaultAddress={{
            street_number: property.street_number,
            route: property.route,
            postcode: property.postcode,
            locality: property.locality,
            state: property.state,
            country: property.country,
            country_code: property.country_code,
            lat: property.lat,
            lng: property.lng,
          }}
          onClose={() => setOpen(false)}
          onSaved={onPropertyUpdate}
        />
      )}
    </Box>
  );
};
